import { useState, useEffect, useRef } from 'react';

import { reviewTemplate } from '@app/components/EmailTemplateEditor/templates';
import clsx from 'clsx';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';
import emptyLogo from '@app/assets/images/empty-logo.png';
 
import { AIDropdownMinimal } from '@app/components/AIDropdown/AIDropdownMinimal';
import { VariableDropdown } from '@app/components/VariableDropdown/VariableDropdown';
import { 
  improveText, 
  fixGrammarAndSpelling, 
  makeTextConcise, 
  makeTextLonger, 
  regenerateText,
  changeTone
} from '@app/services/service';
import ChangeToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GrammarIcon from '@app/assets/icons/ai/grammar.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import TextLongerIcon from '@app/assets/icons/ai/text-longer.svg?react';
import Edit03Icon from '@app/assets/icons/edit-03.svg?react';

// Helper function to insert variable at cursor position
const insertVariableAtCursor = (
  currentValue: string,
  variable: string,
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
): { newValue: string; cursorPosition: number } => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
    const cursorPosition = start + variable.length;
    return { newValue, cursorPosition };
  }
  const newValue = currentValue + variable;
  return { newValue, cursorPosition: newValue.length };
};

// Helper function to create tone options for Change tone dropdown
const createToneOptions = (
  value: string,
  handleAIAction: (action: () => Promise<any>) => Promise<void>
) => {
  const tones = [
    { label: 'Gratitude', tone: 'gratitude' },
    { label: 'Improving', tone: 'improving' },
    { label: 'Exclusive', tone: 'exclusive' },
    { label: 'Making a difference', tone: 'making-a-difference' },
    { label: 'Quick & easy', tone: 'quick-and-easy' },
    { label: 'Partnership', tone: 'partnership' },
    { label: 'Rewarding', tone: 'rewarding' },
    { label: 'Personalized', tone: 'personalized' }
  ];

  return tones.map(({ label, tone }) => ({
    label,
    Icon: <Edit03Icon className="w-4 h-4" />,
    onClick: () => handleAIAction(() => changeTone(value, tone))
  }));
};

// Individual form field components
const EmailSubjectField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [showAIDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, inputRef);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Email Subject</h4>
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 max-w-md p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          placeholder="How was your experience?"
        />
        <div className="flex gap-1 items-center flex-shrink-0">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal     
              actions={aiActions}
              showOnClick={showAIDropdown}
              onlyIcon={true}
            />
          </div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>
    </div>
  );
};

const BusinessLogoField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div>
    <h4 className="text-sm font-medium text-gray-700 mb-3">Company Logo</h4>
    <div className="flex items-center gap-3">
      <div className="w-36 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white">
        {value ? (
          <img src={value} alt="Logo" className="w-full h-full object-cover" />
        ) : (
          <img src={emptyLogo} alt="Empty Logo" className="w-full h-full object-cover" />
        )}
      </div>
      <button
        className="px-3 py-2 text-sm rounded-lg bg-[#E7EAF4] transition-colors"
        onClick={() => document.getElementById('email-logo-upload-inline')?.click()}
        type="button"
      >
        Choose photo
      </button>
      <input
        id="email-logo-upload-inline"
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
              onChange(reader.result as string);
            };
            reader.readAsDataURL(file);
          }
          e.target.value = '';
        }}
        className="hidden"
      />
    </div>
  </div>
);

const HeadingField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [showAIDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, inputRef);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;

    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Heading</h4>
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 max-w-md p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          placeholder="How was your experience?"
        />
        <div className="flex gap-1 items-center">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal 
              actions={aiActions}
              showOnClick={showAIDropdown}
              onlyIcon={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [showAIDropdown] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, textareaRef);
    onChange(newValue);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;

    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Make it longer',
      Icon: <TextLongerIcon />,
      onClick: () => handleAIAction(() => makeTextLonger(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Message</h4>
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 max-w-md p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
          rows={4}
          placeholder="Hi {{customer-first-name}},&#10;At {{business-name}}, we're always working to&#10;improve our product service, and we need&#10;your help to do just that.&#10;If you have a moment, please leave us a&#10; review by clicking below."
        />
        <div className="flex gap-1 items-center flex-shrink-0 mb-1">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal 
              actions={aiActions}
              showOnClick={showAIDropdown}  
              onlyIcon={true}
            />
          </div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>
    </div>
  );
};

const ButtonTextField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const [showAIDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, inputRef);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Text on button</h4>
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 max-w-md p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          placeholder="Share feedback"
        />
        <div className="flex gap-1 items-center flex-shrink-0">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal 
              actions={aiActions}
              showOnClick={showAIDropdown}
              onlyIcon={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ButtonColorField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div>
    <h4 className="text-sm font-medium text-gray-700 mb-3">Button Color</h4>
    <div className="space-y-3">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        placeholder="Custom #HEX"
      />
      <div className="flex gap-2">
        {['#3b82f6', '#1f2937', '#60a5fa', '#0d9488', '#10b981', '#f59e0b', '#f97316', '#ef4444', '#ec4899', '#8b5cf6', '#ffffff'].map((color) => (
          <button
            key={color}
            onClick={() => onChange(color)}
            className={clsx(
              'w-6 h-6 rounded-lg border-2 transition-all',
              value === color ? 'border-gray-400 scale-110' : 'border-gray-300'
            )}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  </div>
);

// Export the inline editor component
export const EmailInlineEditor = ({ 
  content, 
  onChange 
}: { 
  content: {
    subject: string;
    heading: string;
    message: string;
    buttonText: string;
    buttonColor: string;
    logoUrl: string;
  };
  onChange: (content: any) => void;
}) => (
  <div className="space-y-6">
    <EmailSubjectField 
      value={content.subject} 
      onChange={(value) => onChange({ ...content, subject: value })} 
    />
    <BusinessLogoField 
      value={content.logoUrl} 
      onChange={(value) => onChange({ ...content, logoUrl: value })} 
    />
    <HeadingField 
      value={content.heading} 
      onChange={(value) => onChange({ ...content, heading: value })} 
    />
    <MessageField 
      value={content.message} 
      onChange={(value) => onChange({ ...content, message: value })} 
    />
    <ButtonTextField 
      value={content.buttonText} 
      onChange={(value) => onChange({ ...content, buttonText: value })} 
    />
    <ButtonColorField 
      value={content.buttonColor} 
      onChange={(value) => onChange({ ...content, buttonColor: value })} 
    />
  </div>
);

interface EmailDesignEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: string, editedHtml: string) => void;
  initialTemplate?: string;
}

interface EmailContent {
  subject: string;
  heading: string;
  message: string;
  buttonText: string;
  buttonColor: string;
  logoUrl?: string;
}

const defaultEmailContent: EmailContent = {
  subject: 'How was your experience?',
  heading: 'How was your experience?',
  message: `Hi {{customer-first-name}}, 
At {{business-name}}, we're always working to improve our product service, and we need your help to do just that. 
If you have a moment, please leave us a review by clicking below.`,
  buttonText: 'Share feedback',
  buttonColor: '#007bff',
};

const colorOptions = [
  '#007bff', // blue
  '#000000', // black
  '#666666', // dark gray
  '#28a745', // light green
  '#ffc107', // yellow
  '#fd7e14', // orange
  '#dc3545', // red
  '#e83e8c', // pink
  '#6f42c1', // purple
];

export const EmailDesignEditor = ({
  open,
  onClose,
  onSave,
  initialTemplate = reviewTemplate,
}: EmailDesignEditorProps) => {
  const dispatch = useAppDispatch();
  const [emailContent, setEmailContent] = useState<EmailContent>(defaultEmailContent);
  const [previewTemplate, setPreviewTemplate] = useState(initialTemplate);


  // Load saved content when dialog opens
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem('campaignFormData');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.emailDesignContent) {
          setEmailContent(parsed.emailDesignContent as EmailContent);
        }
      }
    } catch {
      // ignore
    }
  }, [open]);

  // Generate preview template based on email content
  useEffect(() => {
    const generateTemplate = () => {
      const template = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
}
.container {
  background-color: #fff;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}
.content {
  text-align: left;
  margin: 16px 0;
}
.button {
  background-color: ${emailContent.buttonColor};
  color: #ffffff;
  padding: 10px 20px;
  text-decoration: none;
  border-radius: 5px;
  display: inline-block;
  margin: 20px 0 0 0;
  font-weight: bold;
}
.button-container {
  text-align: center;
  margin: 20px 0;
}
.footer {
  font-size: 12px;
  color: #888;
  margin-top: 30px;
  text-align: center;
  background: transparent;
  border: none;
  padding: 0;
}
.logo-container {
  text-align: center;
  margin-bottom: 20px;
}
.logo-container img {
  max-width: 100%;
  height: auto;
  display: block;
  border: 0;
  margin: 0 auto;
}
</style>
</head>
<body>
  <div class="container">
    <div class="logo-container">
      ${emailContent.logoUrl ? 
        `<img src="${emailContent.logoUrl}" alt="Business Logo" width="200" height="100" style="object-fit: contain;"/>` : 
        '<div style="text-align: center; font-weight: bold; color: #666;">LOGO</div>'
      }
    </div>
    <div class="content">
      <h2>${emailContent.heading}</h2>
      <p>${emailContent.message.replace(/\n/g, '<br>')}</p>
    </div>
    <div class="button-container">
      <a href="{{buttonUrl}}" class="button" style="color: #ffffff !important;">
        ${emailContent.buttonText}
      </a>
    </div>
    <div class="footer">
      <p>You are receiving this email because you opted in via our website.</p>
      <p>{{businessAddress}}</p>
      <a href="#">Unsubscribe</a>
      <div style="margin-top: 20px; font-size: 10px; color: #999;">Powered by EchoRatings</div>
    </div>
  </div>
</body>
</html>`;
      
      setPreviewTemplate(template);
    };

    generateTemplate();
  }, [emailContent]);

  const handleSave = () => {
    // Save to localStorage immediately
    const storedData = localStorage.getItem('campaignFormData');
    const parsedData = storedData ? JSON.parse(storedData) : {};
    
    // Generate text template from email content
    const textTemplate = `${emailContent.heading}\n\n${emailContent.message}\n\n${emailContent.buttonText}`;
    
    // Preserve existing form data and only update template-related fields
    const updatedData = {
      ...parsedData,
      updatedEmailTemplate: previewTemplate, // This should be the HTML template
      updatedTextTemplate: textTemplate,
      emailDesignContent: emailContent,
    };
    
    localStorage.setItem('campaignFormData', JSON.stringify(updatedData));
        
    // Dispatch to Redux
    dispatch(updateFormData(updatedData));
    
    onSave(initialTemplate, previewTemplate);
    onClose();
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setEmailContent(prev => ({ ...prev, logoUrl: base64String }));
      };
      reader.onerror = () => {
        console.error('Error reading file');
      };
      reader.readAsDataURL(file);
      // allow re-selecting the same file consecutively
      e.target.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setEmailContent(prev => ({ ...prev, logoUrl: undefined }));
  };








    if (!open) return null;
  
  return (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
        <div className="flex flex-col w-full h-full bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-bold text-gray-900">Design settings</h2>
          </div>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Save & continue
          </button>
        </div>

        {/* Subtitle */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Customize the design of the main email that will be sent to the customer with a request for review.
          </p>
        </div>

        {/* Divider Line */}
        <div className="border-b border-gray-200"></div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Design Settings */}
          <div className="w-[70%] pl-6 pr-4 py-4 overflow-y-auto border-r border-gray-200">
            <div className="space-y-6">
              {/* Email Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Subject2
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={emailContent.subject}
                    onChange={(e) => setEmailContent(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="How was your experience?"
                    className="flex-1 max-w-lg p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="text-sm font-mono">&lt;/&gt;</span>
                  </button>
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    ✨
                  </button>
                </div>
              </div>

              {/* Divider Line */}
              <div className="border-b border-gray-200"></div>

              {/* Company Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Logo
                </label>
                <div className="flex items-center gap-3">
                  <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 relative overflow-hidden">
                    {emailContent.logoUrl ? (
                      <>
                        <img
                          src={emailContent.logoUrl}
                          alt="Company Logo"
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                          title="Remove logo"
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <img src={emptyLogo} alt="Empty Logo" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="email-logo-upload-unique"
                    />
                    <label
                      htmlFor="email-logo-upload-unique"
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm font-medium"
                    >
                      Choose photo
                    </label>
                    {emailContent.logoUrl && (
                      <button
                        onClick={handleRemoveLogo}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Heading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heading
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={emailContent.heading}
                    onChange={(e) => setEmailContent(prev => ({ ...prev, heading: e.target.value }))}
                    placeholder="How was your experience?"
                    className="flex-1 max-w-lg p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="text-sm font-mono">&lt;/&gt;</span>
                  </button>
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    ✨
                  </button>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <div className="relative">
                  <textarea
                    value={emailContent.message}
                    onChange={(e) => setEmailContent(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Enter your message here..."
                    rows={6}
                    className="w-full max-w-lg p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
                  />
                  <div className="absolute bottom-2 right-4 flex gap-1">
                    <button className="border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white">
                      <span className="text-sm font-mono">&lt;/&gt;</span>
                    </button>
                    <button className="border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white">
                      ✨
                    </button>
                  </div>
                </div>
              </div>

              {/* Divider Line */}
              <div className="border-b border-gray-200"></div>

              {/* Text on Button */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text on button
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={emailContent.buttonText}
                    onChange={(e) => setEmailContent(prev => ({ ...prev, buttonText: e.target.value }))}
                    placeholder="Share feedback"
                    className="flex-1 max-w-lg p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="text-sm font-mono">&lt;/&gt;</span>
                  </button>
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    ✨
                  </button>
                </div>
              </div>

              {/* Button Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Color
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={emailContent.buttonColor}
                    onChange={(e) => setEmailContent(prev => ({ ...prev, buttonColor: e.target.value }))}
                    placeholder="Custom #HEX"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  />
                  <div className="flex gap-2 flex-wrap">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        onClick={() => setEmailContent(prev => ({ ...prev, buttonColor: color }))}
                        className={clsx(
                          'w-8 h-8 rounded-full border-2 transition-colors',
                          emailContent.buttonColor === color ? 'border-blue-500' : 'border-gray-300'
                        )}
                        style={{ backgroundColor: color }}
                        aria-label={`Select color ${color}`}
                      />
                    ))}
                    <button
                      className="w-8 h-8 rounded-full border-2 border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                      aria-label="Custom color"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="w-[30%] pl-4 pr-6 py-4 overflow-y-auto bg-gray-50">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
            </div>
            <div className="bg-white rounded-lg overflow-hidden">
              <iframe
                srcDoc={previewTemplate}
                title="Email Preview"
                className="w-full h-[600px] border-0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
